import {
  DiceHomeItem,
  DiceResultItem,
  getDiceHomeList,
  pageDiceResult,
} from '@/common-pages/dice/dice-service';
import React from 'react';
import {useTranslation} from 'react-i18next';
import ResultTab from '../components/result-tab/result-tab';
import Spin from '@/components/basic/spin';
import theme from '@/style';
import {ScrollView, View, StyleSheet} from 'react-native';
import Text from '@/components/basic/text';
import ResultItem from '@/common-pages/dice/dice-tab/result/result-item';
import DiceDraw from './draw';
import {currentBrand} from '@/brands';
import NoData from '@/components/basic/error-pages/no-data';
import DrawResult from './result';
import {packageId} from '@/config';
import ShowMoreButton from '../components/show-more-button';
const pageSize = 10;

const toHeaderLabel = (label: string) =>
  label.replace(/\b\w/g, letter => letter.toUpperCase());

const DiceResult = ({isActive}: {isActive?: boolean}) => {
  const {t} = useTranslation();
  const [times, setTimes] = React.useState<DiceHomeItem[]>([]);
  const [configId, setConfigId] = React.useState<string>();
  const [loading, setLoading] = React.useState(false);
  const [firstLoad, setFirstLoad] = React.useState(true);
  const [total, setTotal] = React.useState(0);
  const [list, setList] = React.useState<DiceResultItem[]>([]);
  const currentPage = React.useRef(1);
  const listRef = React.useRef<DiceResultItem[]>([]);
  const getTimes = async () => {
    try {
      const res = await getDiceHomeList();
      setTimes(res);
      if (res) {
        setConfigId(`${res[0].configId}`);
      }
    } finally {
    }
  };

  const getList = async (id: number, page: number = 1, loadMore = false) => {
    try {
      setLoading(true);
      const res = await pageDiceResult({
        configId: id,
        pageNo: page,
        pageSize,
      });
      if (res) {
        setTotal(res.totalSize);
        const currentList = res.content || [];
        const nextList = loadMore
          ? listRef.current.concat(currentList)
          : currentList;
        listRef.current = nextList;
        setList(nextList);
      }
    } finally {
      setLoading(false);
      setFirstLoad(false);
    }
  };

  React.useEffect(() => {
    if (isActive) {
      getTimes();
    }
  }, [isActive]);

  React.useEffect(() => {
    if (configId && isActive) {
      currentPage.current = 1;
      listRef.current = [];
      setList([]);
      getList(+configId);
    }
  }, [configId, isActive]);

  const handleMore = () => {
    if (!configId || loading || list.length >= total) {
      return;
    }
    currentPage.current += 1;
    getList(+configId, currentPage.current, true).then();
  };

  return (
    <Spin loading={loading} style={[theme.flex.flex1]}>
      <ScrollView
        stickyHeaderIndices={[1]}
        scrollEventThrottle={16}
        contentContainerStyle={styles.scrollContent}>
        {!firstLoad && configId && isActive ? (
          <DiceDraw configId={+configId} />
        ) : (
          <View />
        )}
        <ResultTab
          selectKey={configId}
          onSelectKey={setConfigId}
          textWrapClassName="text-[#2B3240]"
          minuteTextClassName="text-[#5C6575]"
          tabs={times.map(item => {
            return {
              label: `${item.cycle}`,
              key: `${item.configId}`,
            };
          })}
        />
        {!firstLoad && configId && isActive ? (
          <DrawResult configId={+configId} />
        ) : null}
        {total > 0 ? (
          <View style={styles.tableWrap}>
            <View style={[styles.tableHeader]}>
              <View style={styles.tableHeaderInner}>
                <View style={[styles.issueCol, theme.flex.center]}>
                  <Text style={styles.tableHeaderText}>
                    {packageId === 101
                      ? toHeaderLabel(t('bets-detail.label.issue'))
                      : toHeaderLabel(t('bets-page.label.drawTime'))}
                  </Text>
                </View>
                <View style={[styles.resultCol, theme.flex.center]}>
                  <Text style={styles.tableHeaderText}>
                    {toHeaderLabel(t('bets-detail.label.result'))}
                  </Text>
                </View>
                <View style={[styles.sumCol, theme.flex.center]}>
                  <Text style={styles.tableHeaderText}>
                    {toHeaderLabel(t('bets-detail.label.sum'))}
                  </Text>
                </View>
                <View style={[styles.valueCol, theme.flex.center]}>
                  <Text style={styles.tableHeaderText}>
                    {toHeaderLabel(t('bets-detail.label.value'))}
                  </Text>
                </View>
                <View style={[styles.numberCol, theme.flex.center]}>
                  <Text style={styles.tableHeaderText}>
                    {toHeaderLabel(t('bets-detail.label.number'))}
                  </Text>
                </View>
              </View>
            </View>
            {list.map((item, _i) => (
              <ResultItem key={_i} index={_i} {...item} />
            ))}
            {list.length < total ? (
              <View style={styles.moreWrap}>
                <ShowMoreButton
                  onPress={handleMore}
                  label={t('home.label.showMore')}
                />
              </View>
            ) : null}
          </View>
        ) : !firstLoad && !loading ? (
          <NoData />
        ) : null}
      </ScrollView>
    </Spin>
  );
};

const styles = StyleSheet.create({
  scrollContent: {
    paddingBottom: 72,
  },
  tableHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#E9ECF5',
    paddingVertical: 8,
  },
  tableHeaderInner: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginHorizontal: 12,
  },
  tableWrap: {
    marginHorizontal: 12,
    marginBottom: 24,
    borderRadius: 8,
    overflow: 'hidden',
  },
  tableHeaderText: {
    color: currentBrand.key === 'anna' ? '#31373D' : '#191919',
    fontSize: 12,
    lineHeight: 16,
    letterSpacing: -0.3,
  },
  issueCol: {
    width: 72,
  },
  resultCol: {
    width: 72,
  },
  sumCol: {
    width: 27,
  },
  valueCol: {
    width: 38,
  },
  numberCol: {
    width: 50,
  },
  moreWrap: {
    backgroundColor: '#FFFFFF',
    padding: 12,
  },
});

export default DiceResult;
